import {
  guardSignalOfFaultKind,
  LEG_FAULT_RECOVER_SOURCE_CLI_PROBE,
  LEG_FAULT_RECOVER_SOURCE_EXPIRY,
  LEG_FAULT_RECOVER_SOURCE_MODE_OFF,
  LEG_FAULT_RECOVER_SOURCE_OPS_RESET,
  PROBE_EXTERNAL_TRIP_EXPIRY_MINUTES,
  type ProbeLevel,
} from '@nadoutong/contracts';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { qualityEvidence } from './qualityEvidence.ts';

export type EventChipTone = 'ok' | 'warn' | 'bad' | 'neutral';

export interface EventChip {
  readonly tone: EventChipTone;
  readonly labelKey: I18nKey | null;
}

function textOf(details: Readonly<Record<string, unknown>>, key: string): string | null {
  const value = details[key];
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

type EventReason = 'probe' | 'quality' | 'chain' | 'jev' | 'silent' | 'broken' | 'guard';

/**
 * Trip reason of a probe / fault event. guard-v2's new fault kinds (overload,
 * first_content_stream, first_content_nonstream, prelude_fail) read as `guard`;
 * the detail line names the signal.
 */
function reasonOf(
  details: Readonly<Record<string, unknown>> | null,
  key: 'trip_reason' | 'shadow_trip_reason',
): EventReason | null {
  if (details === null) return null;
  const value = details[key];
  if (
    value === 'probe' || value === 'quality' || value === 'chain'
    || value === 'jev' || value === 'silent' || value === 'broken'
  ) {
    return value;
  }
  if (typeof value === 'string' && guardSignalOfFaultKind(value) !== undefined) return 'guard';
  return null;
}

function actionOf(details: Readonly<Record<string, unknown>> | null): string | null {
  if (details === null) return null;
  const value = details.action_taken;
  return typeof value === 'string' ? value : null;
}

function levelOf(details: Readonly<Record<string, unknown>> | null, key: 'from' | 'to' | 'level'): ProbeLevel | null {
  if (details === null) return null;
  const value = details[key];
  return value === 'normal' || value === 'alert' || value === 'war' ? value : null;
}

function chip(tone: EventChipTone, labelKey: I18nKey): EventChip {
  return { tone, labelKey };
}

export function eventChip(
  kind: string,
  details: Readonly<Record<string, unknown>> | null,
): EventChip {
  switch (kind) {
    case 'target_probe_recover':
      // An operator reset writes one per member row, tripped or not (#2658 X2).
      if (details !== null && details.source === LEG_FAULT_RECOVER_SOURCE_OPS_RESET) {
        return chip('neutral', 'guaranteed.events.chip.ops_reset');
      }
      if (reasonOf(details, 'trip_reason') === 'silent' || textOf(details ?? {}, 'original_trip_reason') === 'silent') {
        return chip('ok', 'guaranteed.events.chip.silent_recover');
      }
      if (reasonOf(details, 'trip_reason') === 'broken' || textOf(details ?? {}, 'original_trip_reason') === 'broken') {
        return chip('ok', 'guaranteed.events.chip.broken_recover');
      }
      if (reasonOf(details, 'trip_reason') === 'guard') return chip('ok', 'guaranteed.events.chip.guard_recover');
      return chip('ok', 'guaranteed.events.chip.probe_recover');
    case 'shadow_recover':
      if (reasonOf(details, 'shadow_trip_reason') === 'silent') {
        return chip('ok', 'guaranteed.events.chip.shadow_silent_recover');
      }
      if (reasonOf(details, 'shadow_trip_reason') === 'broken') {
        return chip('ok', 'guaranteed.events.chip.shadow_broken_recover');
      }
      if (reasonOf(details, 'shadow_trip_reason') === 'guard') {
        return chip('ok', 'guaranteed.events.chip.shadow_guard_recover');
      }
      return chip('ok', 'guaranteed.events.chip.shadow_recover');
    case 'target_probe_trip': {
      const reason = reasonOf(details, 'trip_reason');
      if (reason === 'chain') return chip('bad', 'guaranteed.events.chip.chain_trip');
      if (reason === 'jev') return chip('bad', 'guaranteed.events.chip.jev_trip');
      if (reason === 'quality') return chip('bad', 'guaranteed.events.chip.quality_trip');
      if (reason === 'silent') return chip('bad', 'guaranteed.events.chip.silent_trip');
      if (reason === 'broken') return chip('bad', 'guaranteed.events.chip.broken_trip');
      if (reason === 'guard') return chip('bad', 'guaranteed.events.chip.guard_trip');
      return chip('bad', 'guaranteed.events.chip.probe_trip');
    }
    case 'shadow_trip': {
      const reason = reasonOf(details, 'shadow_trip_reason');
      if (reason === 'chain') return chip('warn', 'guaranteed.events.chip.shadow_chain');
      if (reason === 'jev') return chip('warn', 'guaranteed.events.chip.shadow_jev');
      if (reason === 'quality') return chip('warn', 'guaranteed.events.chip.shadow_quality');
      if (reason === 'silent') return chip('warn', 'guaranteed.events.chip.shadow_silent');
      if (reason === 'broken') return chip('warn', 'guaranteed.events.chip.shadow_broken');
      if (reason === 'guard') return chip('warn', 'guaranteed.events.chip.shadow_guard');
      return chip('warn', 'guaranteed.events.chip.shadow_probe');
    }
    case 'guaranteed_shadow_enter':
      return chip('warn', 'guaranteed.events.chip.shadow_enter');
    case 'probe_level': {
      const to = levelOf(details, 'to');
      if (to === 'war') return chip('bad', 'guaranteed.events.chip.level_war');
      if (to === 'alert') {
        return levelOf(details, 'from') === 'war'
          ? chip('warn', 'guaranteed.events.chip.level_war_end')
          : chip('warn', 'guaranteed.events.chip.level_alert');
      }
      if (to === 'normal') return chip('ok', 'guaranteed.events.chip.level_normal');
      return chip('neutral', 'guaranteed.events.chip.level_change');
    }
    case 'probe_war_blocked':
      return chip('neutral', 'guaranteed.events.chip.war_blocked');
    case 'trip_skipped_immune':
      return chip('neutral', 'guaranteed.events.chip.immune');
    case 'target_probe_skip':
      if (details !== null && details.phase === 'recover_wave') {
        return chip(details.clean === true ? 'ok' : 'bad', 'guaranteed.events.chip.recover_wave');
      }
      return { tone: 'neutral', labelKey: null };
    // #2744: a cut moves the request to the next leg and demotes nothing — informational.
    case 'no_response_cutoff':
      return chip('neutral', 'guaranteed.events.chip.no_response_cutoff');
    // R1-B1: an S2 / S3 shadow observation; nothing was cut.
    case 'first_content_would_cut':
      return chip('warn', 'guaranteed.events.chip.first_content_would_cut');
    case 'stall_cutoff':
      if (details !== null && details.shadow === true) {
        return chip('warn', 'guaranteed.events.chip.stall_cutoff_shadow');
      }
      return chip('neutral', 'guaranteed.events.chip.stall_cutoff');
    case 'chain_trip_suppressed':
      return chip('neutral', 'guaranteed.events.chip.chain_suppressed');
    case 'judge_error':
      return chip('neutral', 'guaranteed.events.chip.judge_error');
    case 'jev_verdict': {
      const action = actionOf(details);
      if (action === 'trip') return chip('bad', 'guaranteed.events.chip.jev_verdict_trip');
      if (action === 'would_trip') return chip('warn', 'guaranteed.events.chip.jev_verdict_would');
      if (action === 'judge_error') return chip('neutral', 'guaranteed.events.chip.judge_error');
      return chip('neutral', 'guaranteed.events.chip.jev_verdict_record');
    }
    default:
      return { tone: 'neutral', labelKey: null };
  }
}

export type DetailTranslate = (key: I18nKey, params?: Readonly<Record<string, string>>) => string;

const TRIP_LEVEL_KEYS: Readonly<Record<ProbeLevel, I18nKey>> = {
  normal: 'guaranteed.events.detail.trip_level.normal',
  alert: 'guaranteed.events.detail.trip_level.alert',
  war: 'guaranteed.events.detail.trip_level.war',
};

const SHADOW_TRIP_LEVEL_KEYS: Readonly<Record<ProbeLevel, I18nKey>> = {
  normal: 'guaranteed.events.detail.shadow_level.normal',
  alert: 'guaranteed.events.detail.shadow_level.alert',
  war: 'guaranteed.events.detail.shadow_level.war',
};

function recordOf(details: Readonly<Record<string, unknown>>, key: string): Readonly<Record<string, unknown>> | null {
  const value = details[key];
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return Object.fromEntries(Object.entries(value));
}

/** 「4/4、3/4」: failed / judged shots per wave; null when absent or malformed. */
function wavesText(raw: unknown): string | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const parts: string[] = [];
  for (const wave of raw) {
    if (typeof wave !== 'object' || wave === null) return null;
    const entry = new Map<string, unknown>(Object.entries(wave));
    const shots = entry.get('shots');
    const failed = entry.get('failed');
    if (typeof shots !== 'number' || typeof failed !== 'number') return null;
    parts.push(`${String(failed)}/${String(shots)}`);
  }
  return parts.join(' ');
}

function clock(iso: string): string {
  return formatLocalTimestamp(iso).slice(11, 16);
}

/**
 * `probe_level` / `probe_war_blocked` (#2658 follow-up): why the level moved
 * (real-traffic failures, a Jev verdict, the war timer, calm, or a trigger
 * held back by the cooldown), the waves a war ended on, and the cooldown.
 */
function levelDetailLine(details: Readonly<Record<string, unknown>>, t: DetailTranslate): string {
  const parts: string[] = [];
  const source = textOf(details, 'source');
  const trigger = recordOf(details, 'trigger');
  const failures = trigger === null ? null : textOf(trigger, 'failures');
  const windowSeconds = trigger === null ? null : textOf(trigger, 'window_s');
  if (textOf(details, 'outcome') === 'aborted') {
    // The second abort in a row cools like a finished war (red-team P2-1).
    parts.push(t(textOf(details, 'cooldown_until') === null
      ? 'guaranteed.events.detail.source.aborted'
      : 'guaranteed.events.detail.source.aborted_cooled'));
  } else if (source === 'traffic' && failures !== null && windowSeconds !== null) {
    parts.push(t('guaranteed.events.detail.source.traffic', { failures, seconds: windowSeconds }));
  } else if (source === 'signature' && trigger !== null && textOf(trigger, 'signature') !== null) {
    parts.push(t('guaranteed.events.detail.source.signature', {
      status: textOf(trigger, 'status') ?? '4xx',
      signature: textOf(trigger, 'signature') ?? '',
    }));
  } else if (source === 'calm') {
    parts.push(t(levelOf(details, 'from') === 'war' ? 'guaranteed.events.detail.source.good_waves' : 'guaranteed.events.detail.source.calm'));
  } else if (source !== null) {
    const key = SOURCE_KEYS[source];
    parts.push(key === undefined ? source : t(key));
  }
  const waves = wavesText(details['waves']);
  if (waves !== null) parts.push(t('guaranteed.events.detail.waves', { waves }));
  const cooldown = textOf(details, 'cooldown_until');
  if (cooldown !== null) {
    parts.push(t('guaranteed.events.detail.cooldown', {
      until: clock(cooldown),
      n: textOf(details, 'war_entries_window') ?? '?',
    }));
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

const SOURCE_KEYS: Readonly<Record<string, I18nKey>> = {
  traffic: 'guaranteed.events.detail.source.traffic_plain',
  jev: 'guaranteed.events.detail.source.jev',
  signature: 'guaranteed.events.detail.source.signature_plain',
  breaker: 'guaranteed.events.detail.source.breaker',
  timer: 'guaranteed.events.detail.source.timer',
  cooldown_block: 'guaranteed.events.detail.source.cooldown_block',
  off: 'guaranteed.events.detail.source.off',
  removed: 'guaranteed.events.detail.source.removed',
  reset: 'guaranteed.events.detail.source.reset',
  [LEG_FAULT_RECOVER_SOURCE_CLI_PROBE]: 'guaranteed.events.detail.source.cli_probe',
  [LEG_FAULT_RECOVER_SOURCE_EXPIRY]: 'guaranteed.events.detail.source.expiry',
  [LEG_FAULT_RECOVER_SOURCE_MODE_OFF]: 'guaranteed.events.detail.source.mode_off',
  [LEG_FAULT_RECOVER_SOURCE_OPS_RESET]: 'guaranteed.events.chip.ops_reset',
};

const STALL_HANDLING_KEYS: Readonly<Record<string, I18nKey>> = {
  A: 'guaranteed.events.detail.handling.a',
  B: 'guaranteed.events.detail.handling.b',
  C: 'guaranteed.events.detail.handling.c',
  would_cut: 'guaranteed.events.detail.handling.would_cut',
};

const REASON_KEYS: Readonly<Record<string, I18nKey>> = {
  probe: 'guaranteed.leg.trigger.probe',
  chain: 'guaranteed.leg.trigger.chain',
  jev: 'guaranteed.leg.trigger.jev',
  quality: 'guaranteed.leg.trigger.quality',
  silent: 'guaranteed.leg.trigger.silent',
  broken: 'guaranteed.leg.trigger.broken',
  overload: 'guaranteed.leg.trigger.overload',
  first_content_stream: 'guaranteed.leg.trigger.first_content_stream',
  first_content_nonstream: 'guaranteed.leg.trigger.first_content_nonstream',
  prelude_fail: 'guaranteed.leg.trigger.prelude_fail',
  probe_disabled: 'guaranteed.events.reason.probe_disabled',
  immune: 'guaranteed.events.reason.immune',
};

const VERDICT_KEYS: Readonly<Record<string, I18nKey>> = {
  upstream_server: 'guaranteed.events.verdict.upstream_server',
  client: 'guaranteed.events.verdict.client',
  unknown: 'guaranteed.events.verdict.unknown',
};

/** Signatures carry `` field separators and a hash tail; keep the readable head. */
function readableSignature(raw: string): string {
  const head = raw.split('').map((part) => part.trim()).filter((part) => part.length > 0);
  const named = head.filter((part) => !/^[0-9a-f]{12,}$/i.test(part));
  return (named.length > 0 ? named : head).join(' / ');
}

const FLAP_KEYS = {
  probe: {
    hold: 'guaranteed.events.detail.flap',
    capped: 'guaranteed.events.detail.flap_capped',
    no_probe: 'guaranteed.events.detail.flap_no_probe',
  },
  expiry: {
    hold: 'guaranteed.events.detail.flap_expiry',
    capped: 'guaranteed.events.detail.flap_expiry_capped',
    no_probe: 'guaranteed.events.detail.flap_expiry_no_probe',
  },
} as const satisfies Record<'probe' | 'expiry', Record<'hold' | 'capped' | 'no_probe', I18nKey>>;

/**
 * The flap line (red-team P3): the hold delays only a probe release, and
 * the chain / Jev trip also expires 30 minutes after it. So: no probe
 * release at all (probes not act at the trip) → the expiry is the release;
 * a hold at or past the expiry → the expiry comes first; otherwise the hold.
 * An event from before `probe_release` was recorded reads as act.
 */
function flapKey(damping: Readonly<Record<string, unknown>>, holdMinutes: number): I18nKey {
  const keys = FLAP_KEYS[textOf(damping, 'release_source') === 'expiry' ? 'expiry' : 'probe'];
  if (damping['probe_release'] === false) return keys.no_probe;
  return Number.isFinite(holdMinutes) && holdMinutes >= PROBE_EXTERNAL_TRIP_EXPIRY_MINUTES ? keys.capped : keys.hold;
}

function msToSecondsLabel(raw: string): string {
  const value = Number(raw);
  return Number.isFinite(value) ? String(value / 1000) : raw;
}

function byHandlingParts(details: Readonly<Record<string, unknown>>, t: DetailTranslate): readonly string[] {
  const raw = details.by_handling;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return [];
  const rec = Object.fromEntries(Object.entries(raw));
  const parts: string[] = [];
  for (const key of ['A', 'B', 'C', 'would_cut'] as const) {
    const count = rec[key];
    const labelKey = STALL_HANDLING_KEYS[key];
    if (typeof count !== 'number' || count <= 0 || labelKey === undefined) continue;
    parts.push(`${t(labelKey)} ${String(count)}`);
  }
  return parts;
}

function recoverWaveLine(details: Readonly<Record<string, unknown>>, t: DetailTranslate): string {
  const parts: string[] = [];
  const probeKind = textOf(details, 'probe_kind');
  if (probeKind !== null) parts.push(t('guaranteed.events.detail.probe_kind', { kind: probeKind }));
  const wave = textOf(details, 'wave');
  if (wave !== null) parts.push(t('guaranteed.events.detail.wave', { n: wave }));
  const shots = textOf(details, 'shots');
  if (shots !== null) parts.push(t('guaranteed.events.detail.shots', { n: shots }));
  parts.push(t(details.clean === true
    ? 'guaranteed.events.detail.clean'
    : 'guaranteed.events.detail.not_clean'));
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function stallCutoffLine(details: Readonly<Record<string, unknown>>, t: DetailTranslate): string {
  const parts: string[] = [...byHandlingParts(details, t)];
  if (details.shadow === true) parts.push(t('guaranteed.mode.shadow'));
  const cutoff = textOf(details, 'cutoff_ms');
  if (cutoff !== null) parts.push(t('guaranteed.events.detail.cutoff_ms', { seconds: msToSecondsLabel(cutoff) }));
  const silence = textOf(details, 'max_silence_ms');
  if (silence !== null) parts.push(t('guaranteed.events.detail.max_silence', { seconds: msToSecondsLabel(silence) }));
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function opsResetLine(details: Readonly<Record<string, unknown>>, t: DetailTranslate): string {
  const label = (reason: string): string => {
    const key = REASON_KEYS[reason];
    return key === undefined ? reason : t(key);
  };
  const trip = textOf(details, 'original_trip_reason');
  if (trip !== null) return t('guaranteed.events.detail.ops_reset_trip', { by: label(trip) });
  const shadow = textOf(details, 'original_shadow_trip_reason');
  if (shadow !== null) return t('guaranteed.events.detail.ops_reset_shadow', { by: label(shadow) });
  return t('guaranteed.events.detail.ops_reset');
}

/**
 * One operator-readable line from the event's details. Field names come from
 * the gateway load-event writers (`reason`, `fail`/`ok`, `trip_reason`,
 * `verdict`, `signature`, `status`, `mode`, `last_error_excerpt`, ...);
 * anything not covered is still visible in the JSON tooltip.
 */
export function eventDetailLine(
  details: Readonly<Record<string, unknown>> | null,
  t: DetailTranslate,
  kind?: string,
): string {
  if (details === null) return '—';
  if (kind === 'probe_level' || kind === 'probe_war_blocked') return levelDetailLine(details, t);
  if (kind === 'target_probe_recover' && textOf(details, 'source') === LEG_FAULT_RECOVER_SOURCE_OPS_RESET) {
    return opsResetLine(details, t);
  }
  if (kind === 'stall_cutoff') return stallCutoffLine(details, t);
  if (kind === 'target_probe_skip' && textOf(details, 'phase') === 'recover_wave') {
    return recoverWaveLine(details, t);
  }
  const parts: string[] = [];
  const reason = textOf(details, 'reason') ?? textOf(details, 'trip_reason') ?? textOf(details, 'shadow_trip_reason');
  if (reason !== null) {
    const key = REASON_KEYS[reason];
    parts.push(key === undefined ? reason : t(key));
  }
  // guard-v2 org-scoped faults retire the leg for that customer only (02-design §12).
  const scope = recordOf(details, 'scope');
  const scopeOrg = scope === null || scope.kind !== 'org' ? null : textOf(scope, 'id');
  if (scopeOrg !== null) parts.push(t('guaranteed.leg.trigger.org_scope', { org: scopeOrg }));
  const tripLevel = levelOf(details, 'level');
  if (tripLevel !== null) {
    const war = recordOf(details, 'war');
    const waves = war === null ? null : wavesText(war['waves']);
    const seconds = war === null ? null : textOf(war, 'seconds');
    parts.push(t((kind === 'shadow_trip' ? SHADOW_TRIP_LEVEL_KEYS : TRIP_LEVEL_KEYS)[tripLevel]));
    if (waves !== null) parts.push(t('guaranteed.events.detail.waves', { waves }));
    if (seconds !== null) parts.push(t('guaranteed.events.detail.war_seconds', { seconds }));
  }
  // A chain / Jev re-trip right after a release (#2658 F3): how soon, and how
  // long the next probe release now waits.
  const damping = recordOf(details, 'd3_damping');
  if (damping !== null) {
    const after = textOf(damping, 'seconds_after_release');
    const hold = textOf(damping, 'hold_minutes');
    const flap = textOf(damping, 'flap');
    if (after !== null && hold !== null && flap !== null) {
      const byKey = reason === null ? undefined : REASON_KEYS[reason];
      const by = byKey === undefined ? (reason ?? '') : t(byKey);
      parts.push(t(flapKey(damping, Number(hold)), {
        seconds: after, by, hold, flap, expiry: String(PROBE_EXTERNAL_TRIP_EXPIRY_MINUTES),
      }));
    }
  }
  const verdict = textOf(details, 'verdict');
  if (verdict !== null) {
    const key = VERDICT_KEYS[verdict];
    parts.push(key === undefined ? verdict : t(key));
  }
  const mode = textOf(details, 'mode');
  if (mode === 'shadow') parts.push(t('guaranteed.mode.shadow'));
  const fail = textOf(details, 'fail');
  const ok = textOf(details, 'ok');
  if (fail !== null || ok !== null) {
    parts.push(t('guaranteed.events.detail.fail_ok', { fail: fail ?? '0', ok: ok ?? '0' }));
  }
  const hits = textOf(details, 'hit_count') ?? textOf(details, 'hits');
  if (hits !== null) parts.push(t('guaranteed.events.detail.hits', { hits }));
  const orgs = textOf(details, 'org_count') ?? textOf(details, 'orgs');
  if (orgs !== null) parts.push(t('guaranteed.events.detail.orgs', { orgs }));
  const probability = textOf(details, 'p') ?? textOf(details, 'p_upstream_server');
  if (probability !== null) parts.push(`p=${probability}`);
  const http = textOf(details, 'status') ?? textOf(details, 'http_status') ?? textOf(details, 'last_status_code');
  if (http !== null) parts.push(`HTTP ${http}`);
  const rounds = textOf(details, 'rounds') ?? textOf(details, 'consecutive_failed_rounds');
  if (rounds !== null) parts.push(t('guaranteed.events.detail.rounds', { rounds }));
  const signature = textOf(details, 'signature');
  if (signature !== null) parts.push(readableSignature(signature));
  const source = textOf(details, 'source');
  if (
    source === LEG_FAULT_RECOVER_SOURCE_CLI_PROBE
    || source === LEG_FAULT_RECOVER_SOURCE_EXPIRY
    || source === LEG_FAULT_RECOVER_SOURCE_MODE_OFF
  ) {
    const key = SOURCE_KEYS[source];
    if (key !== undefined) parts.push(t(key));
  }
  const message = textOf(details, 'message')
    ?? textOf(details, 'excerpt')
    ?? textOf(details, 'last_error_excerpt')
    ?? textOf(details, 'error');
  if (message !== null) {
    if (/^http_\d{3}$/.test(message)) {
      if (http === null) parts.push(`HTTP ${message.slice(5)}`);
    } else {
      parts.push(qualityEvidence(message, t) ?? message);
    }
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export function detailsJson(details: Readonly<Record<string, unknown>> | null): string {
  if (details === null) return 'null';
  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return 'null';
  }
}
